import { Box, Button, Divider, Stack, Tab, Typography, useTheme } from "@mui/material";
import { FilterTabs } from "@zfp-akademie-a-s/zfp-design";
import { AddIcon, CarIcon, HomeIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import { useGetAssetsOverview, useListImmovableAssetItems, useListMovableAssetItems } from "Infrastructure/ApiZfp/AnalyzerApi";
import type { ImmovableAssetItem, MovableAssetItem } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useContext, useState } from "react";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";
import { ContractsOverview } from "./ContractsOverview";
import { ImmovableAssetItemDrawer } from "./ImmovableAssetItemDrawer";
import { MovableAssetItemDrawer } from "./MovableAssetItemDrawer";

type AssetType = "immovable" | "movable";

export const AssetsAndContractsPane: React.FC = () => {
  const theme = useTheme();
  const { t } = useResource();
  const { readOnly, analysisId, versionId } = useContext(AnalysisDetailContext);

  const [assetType, setAssetType] = useState<AssetType>("immovable");
  const [immovableAssetDrawerOpen, setImmovableAssetDrawerOpen] = useState(false);
  const [immovableAssetData, setImmovableAssetData] = useState<ImmovableAssetItem>();
  const [movableAssetDrawerOpen, setMovableAssetDrawerOpen] = useState(false);
  const [movableAssetData, setMovableAssetData] = useState<MovableAssetItem>();

  const { data: assets } = useGetAssetsOverview(analysisId, versionId);
  const { data: immovableAssetItems } = useListImmovableAssetItems(analysisId, versionId);
  const { data: movableAssetItems } = useListMovableAssetItems(analysisId, versionId);

  const isAggregateMode =
    assetType === "immovable" ? assets?.immovableAssetsEntryMode === "aggregate" : assets?.movableAssetsEntryMode === "aggregate";

  return (
    <>
      <Stack spacing={Spacing.XXXL} paddingBottom={40}>
        <Box display="flex" justifyContent="space-between">
          <Box>
            <Typography variant="h3">{t(Resources.AnalysisDetail.AssetsAndContracts.Title)}</Typography>
            <Typography variant="body2" color="secondary">
              {t(Resources.AnalysisDetail.AssetsAndContracts.Subtitle)}
            </Typography>
          </Box>
          {isAggregateMode && (
            <Button
              variant="contained"
              disabled={readOnly}
              color="secondary"
              startIcon={<AddIcon />}
              onClick={() => {
                if (assetType === "immovable") {
                  setImmovableAssetData(undefined);
                  setImmovableAssetDrawerOpen(true);
                } else {
                  setMovableAssetData(undefined);
                  setMovableAssetDrawerOpen(true);
                }
              }}
            >
              {t(Resources.AnalysisDetail.AssetsAndContracts.AddAsset)}
            </Button>
          )}
        </Box>
        <Stack spacing={Spacing.XL} width={680}>
          <Box>
            <FilterTabs
              value={assetType}
              onChange={(_, value) => {
                setAssetType(value);
              }}
            >
              <Tab
                label={t(Resources.AnalysisDetail.AssetsAndContracts.ImmovableAsset)}
                icon={<HomeIcon style={{ marginRight: theme.spacing(Spacing.M) }} />}
                iconPosition="start"
                value="immovable"
              />
              <Tab
                icon={<CarIcon style={{ marginRight: theme.spacing(Spacing.M) }} />}
                iconPosition="start"
                label={t(Resources.AnalysisDetail.AssetsAndContracts.MovableAsset)}
                value="movable"
              />
            </FilterTabs>
          </Box>
          {assetType === "immovable" &&
            immovableAssetItems?.flatMap((item, index) => {
              const overview = (
                <ContractsOverview
                  key={item.guid}
                  itemId={item.guid}
                  name={item.name}
                  note={item.note}
                  assetType="immovable"
                  assetSubType={item.immovableSubType}
                  propertyInsurance={item.propertyInsurance}
                  onEditAsset={() => {
                    setImmovableAssetData(item);
                    setImmovableAssetDrawerOpen(true);
                  }}
                />
              );
              return index < immovableAssetItems.length - 1 ? [overview, <Divider key={`divider-${item.guid}`} />] : [overview];
            })}
          {assetType === "movable" &&
            movableAssetItems?.flatMap((item, index) => {
              const overview = (
                <ContractsOverview
                  key={item.guid}
                  itemId={item.guid}
                  name={item.name}
                  note={item.note}
                  assetType="movable"
                  assetSubType={item.movableSubType}
                  onEditAsset={() => {
                    setMovableAssetData(item);
                    setMovableAssetDrawerOpen(true);
                  }}
                />
              );
              return index < movableAssetItems.length - 1 ? [overview, <Divider key={`divider-${item.guid}`} />] : [overview];
            })}
        </Stack>
      </Stack>
      <ImmovableAssetItemDrawer
        immovableAsset={immovableAssetData}
        open={immovableAssetDrawerOpen}
        onClose={() => {
          setImmovableAssetDrawerOpen(false);
          setImmovableAssetData(undefined);
        }}
      />
      <MovableAssetItemDrawer
        movableAsset={movableAssetData}
        open={movableAssetDrawerOpen}
        onClose={() => {
          setMovableAssetDrawerOpen(false);
          setMovableAssetData(undefined);
        }}
      />
    </>
  );
};
